// Subtask interface for automation cards
type Subtask = {
  id: string;
  text: string;
  completed: boolean;
  createdAt?: string;
};

// Comment object structure
type Comment = {
  id: string;
  text: string;
  userName: string;
  date: number;
  edit?: boolean; // Only used in UI state, not persisted
};

type Card = {
  _id: string;
  text: string;
  description?: string;
  comment?: Comment[]; // Array of comment objects, not a string
  estimateTime?: string;
  date?: string;
  color?: string;
  issueType?: any;
  sharedUsers: any[];
  // New automation-specific fields
  category?: string;           // Category ID from automationCategories
  subcategory?: string;         // Subcategory ID from automationCategories
  owner?: string;               // Owner user ID (single owner)
  eta?: string;                 // ETA date string
  status?: string;              // Status: not-started, in-progress, in-review, complete
  subtasks?: Subtask[];         // Array of subtasks
  priority?: string;            // Priority: critical, high, medium, low
  labels?: string[];            // Color labels for categorization
  createdAt?: string;           // ISO date when card was created
  statusChangedAt?: string;     // ISO date when card last changed columns
};

type CardsByIdState = Record<string, Card>;

type CardsByIdAction =
  | { type: "SET_CARDS"; payload: Record<string, Card> }
  | { type: "ADD_CARD"; payload: { cardText: string; cardId: string } }
  | { type: "CHANGE_CARD_TEXT"; payload: { cardText: string; cardId: string } }
  | { type: "CHANGE_CARD_ESTIMATE"; payload: { CardEstimateTime: string; cardId: string } }
  | { type: "CHANGE_CARD_COMMENT"; payload: { cardComment: string; cardId: string } }
  | { type: "CHANGE_CARD_DESCRIPTION"; payload: { cardDescription: string; cardId: string } }
  | { type: "CHANGE_CARD_DATE"; payload: { date: string; cardId: string } }
  | { type: "CHANGE_CARD_COLOR"; payload: { color: string; cardId: string } }
  | { type: "CHANGE_CARD_ISSUE_TYPE"; payload: { issueType: any; cardId: string } }
  | { type: "DELETE_CARD"; payload: { cardId: string } }
  | { type: "ASSIGN_USERS_TO_CARD"; payload: { cardId: string; sharedUsers: any[] } }
  | { type: "DELETE_LIST"; payload: { cardIds: string[] } }
  // New automation-specific actions
  | { type: "CHANGE_CARD_CATEGORY"; payload: { category: string; cardId: string } }
  | { type: "CHANGE_CARD_SUBCATEGORY"; payload: { subcategory: string; cardId: string } }
  | { type: "CHANGE_CARD_OWNER"; payload: { owner: string; cardId: string } }
  | { type: "CHANGE_CARD_ETA"; payload: { eta: string; cardId: string } }
  | { type: "CHANGE_CARD_STATUS"; payload: { status: string; cardId: string } }
  | { type: "ADD_SUBTASK"; payload: { cardId: string; subtask: Subtask } }
  | { type: "UPDATE_SUBTASK"; payload: { cardId: string; subtaskId: string; subtask: Partial<Subtask> } }
  | { type: "DELETE_SUBTASK"; payload: { cardId: string; subtaskId: string } }
  | { type: "TOGGLE_SUBTASK"; payload: { cardId: string; subtaskId: string } }
  | { type: "SET_CARD_SUBTASKS"; payload: { cardId: string; subtasks: Subtask[] } }
  | { type: "CHANGE_CARD_PRIORITY"; payload: { priority: string; cardId: string } }
  | { type: "CHANGE_CARD_LABELS"; payload: { labels: string[]; cardId: string } };

const cardsById = (state: CardsByIdState = {}, action: CardsByIdAction): CardsByIdState => {
  switch (action.type) {
    case "SET_CARDS": {
      return {
        ...state,
        ...action.payload
      };
    }
    case "ADD_CARD": {
      const { cardText, cardId, userId } = action.payload;
      return {
        ...state,
        [cardId]: {
          text: cardText,
          _id: cardId,
          userId: userId || null,
          sharedUsers: [],
          // Automation board defaults
          category: "",
          subcategory: "",
          owner: "",
          eta: "",
          status: "not-started",
          subtasks: [],
          priority: "",
          labels: [],
          createdAt: new Date().toISOString(),
          statusChangedAt: new Date().toISOString()
        }
      };
    }
    case "CHANGE_CARD_TEXT": {
      const { cardText, cardId } = action.payload;
      return { ...state, [cardId]: { ...state[cardId], text: cardText } };
    }
    case "CHANGE_CARD_ESTIMATE": {
      const { CardEstimateTime, cardId } = action.payload;
      return {
        ...state,
        [cardId]: { ...state[cardId], estimateTime: CardEstimateTime }
      };
    }
    case "CHANGE_CARD_COMMENT": {
      const { cardComment, cardId } = action.payload;

      return {
        ...state,
        [cardId]: { ...state[cardId], comment: cardComment }
      };
    }
    case "CHANGE_CARD_DESCRIPTION": {
      const { cardDescription, cardId } = action.payload;
      return {
        ...state,
        [cardId]: { ...state[cardId], description: cardDescription }
      };
    }
    case "CHANGE_CARD_DATE": {
      const { date, cardId } = action.payload;
      return { ...state, [cardId]: { ...state[cardId], date } };
    }
    case "CHANGE_CARD_COLOR": {
      const { color, cardId } = action.payload;
      return { ...state, [cardId]: { ...state[cardId], color } };
    }
    case "CHANGE_CARD_ISSUE_TYPE": {
      const { issueType, cardId } = action.payload;
      return { ...state, [cardId]: { ...state[cardId], issueType } };
    }
    case "DELETE_CARD": {
      const { cardId } = action.payload;
      const { [cardId]: deletedCard, ...restOfCards } = state;
      return restOfCards;
    }
    case "ASSIGN_USERS_TO_CARD": {
      const { cardId, sharedUsers } = action.payload;
      return { ...state, [cardId]: { ...state[cardId], sharedUsers } };
    }
    // Find every card from the deleted list and remove it (actually unnecessary since they will be removed from db on next write anyway)
    case "DELETE_LIST": {
      const { cardIds } = action.payload;
      return Object.keys(state)
        .filter(cardId => !cardIds.includes(cardId))
        .reduce((newState, cardId) => ({ ...newState, [cardId]: state[cardId] }), {});
    }
    // New automation-specific action handlers
    case "CHANGE_CARD_CATEGORY": {
      const { category, cardId } = action.payload;
      return { ...state, [cardId]: { ...state[cardId], category } };
    }
    case "CHANGE_CARD_SUBCATEGORY": {
      const { subcategory, cardId } = action.payload;
      return { ...state, [cardId]: { ...state[cardId], subcategory } };
    }
    case "CHANGE_CARD_OWNER": {
      const { owner, cardId } = action.payload;
      return { ...state, [cardId]: { ...state[cardId], owner } };
    }
    case "CHANGE_CARD_ETA": {
      const { eta, cardId } = action.payload;
      return { ...state, [cardId]: { ...state[cardId], eta } };
    }
    case "CHANGE_CARD_STATUS": {
      const { status, cardId } = action.payload;
      return { ...state, [cardId]: { ...state[cardId], status, statusChangedAt: new Date().toISOString() } };
    }
    case "ADD_SUBTASK": {
      const { cardId, subtask } = action.payload;
      const currentSubtasks = state[cardId]?.subtasks || [];
      return {
        ...state,
        [cardId]: { ...state[cardId], subtasks: [...currentSubtasks, subtask] }
      };
    }
    case "UPDATE_SUBTASK": {
      const { cardId, subtaskId, subtask } = action.payload;
      const currentSubtasks = state[cardId]?.subtasks || [];
      const updatedSubtasks = currentSubtasks.map(st =>
        st.id === subtaskId ? { ...st, ...subtask } : st
      );
      return {
        ...state,
        [cardId]: { ...state[cardId], subtasks: updatedSubtasks }
      };
    }
    case "DELETE_SUBTASK": {
      const { cardId, subtaskId } = action.payload;
      const currentSubtasks = state[cardId]?.subtasks || [];
      const updatedSubtasks = currentSubtasks.filter(st => st.id !== subtaskId);
      return {
        ...state,
        [cardId]: { ...state[cardId], subtasks: updatedSubtasks }
      };
    }
    case "TOGGLE_SUBTASK": {
      const { cardId, subtaskId } = action.payload;
      const currentSubtasks = state[cardId]?.subtasks || [];
      const updatedSubtasks = currentSubtasks.map(st =>
        st.id === subtaskId ? { ...st, completed: !st.completed } : st
      );
      return {
        ...state,
        [cardId]: { ...state[cardId], subtasks: updatedSubtasks }
      };
    }
    case "SET_CARD_SUBTASKS": {
      const { cardId, subtasks } = action.payload;
      return { ...state, [cardId]: { ...state[cardId], subtasks } };
    }
    case "CHANGE_CARD_PRIORITY": {
      const { priority, cardId } = action.payload;
      return { ...state, [cardId]: { ...state[cardId], priority } };
    }
    case "CHANGE_CARD_LABELS": {
      const { labels, cardId } = action.payload;
      return { ...state, [cardId]: { ...state[cardId], labels } };
    }
    default:
      return state;
  }
};

export default cardsById;
